import { Observable, of, first } from "rxjs";
import { IScriptParams } from "../../models/IScriptParams";
import { DomMethods } from "./DomMethods";

export class DomScriptUtils {
  private static srcAttribute = "src";
  private static scriptMarkup = "script";

  static insertScript(
    target: string,
    params: IScriptParams,
  ): Observable<HTMLScriptElement> {
    const loaded: HTMLScriptElement = this.isScriptLoaded(params);

    if (loaded) {
      return of(loaded).pipe(first());
    }

    let targetElement: Element = document.getElementsByTagName(target)[0];
    if (!targetElement) {
      targetElement = document.getElementById(target);
    }
    // @ts-expect-error TypeScript doesn't allow you to assign known interfaces to dictionaries
    const script: HTMLScriptElement = this.setMarkupAttributes(
      DomScriptUtils.scriptMarkup,
      // @ts-expect-error TypeScript doesn't allow you to assign known interfaces to dictionaries
      params,
    );
    targetElement.appendChild(script);

    return new Observable<HTMLScriptElement>((subscriber) => {
      const onLoad = () => {
        subscriber.next(script);
        subscriber.complete();
        cleanup();
      };

      const onError = (event: Event) => {
        const errorMessage =
          event.type === "error" && params.integrity
            ? `Script integrity check failed (SRI mismatch): ${params.src}`
            : `Script failed to load: ${params.src}`;

        DomMethods.removeElement(script);
        const error = new Error(errorMessage);
        (error as any).errorType = params.integrity
          ? "SRI_MISMATCH"
          : "SCRIPT_LOAD_ERROR";
        (error as any).src = params.src;
        (error as any).integrity = params.integrity;
        subscriber.error(error);
        cleanup();
      };

      const cleanup = () => {
        script.removeEventListener("load", onLoad);
        script.removeEventListener("error", onError);
      };

      script.addEventListener("load", onLoad);
      script.addEventListener("error", onError);
    }).pipe(first());
  }

  private static isScriptLoaded(
    params: IScriptParams,
  ): HTMLScriptElement | null {
    const { src, id } = params;
    const scriptBySrc: HTMLScriptElement | null =
      document.querySelector<HTMLScriptElement>(
        `${DomScriptUtils.scriptMarkup}[${DomScriptUtils.srcAttribute}="${src}"]`,
      );
    const scriptById: HTMLScriptElement | null = document.getElementById(
      id,
    ) as HTMLScriptElement;
    return scriptById || scriptBySrc;
  }

  private static setMarkupAttributes(
    target: string,
    params: Record<string, string>,
  ): HTMLElement {
    const element: HTMLElement = document.createElement(target);
    Object.keys(params).forEach((param: string) => {
      element.setAttribute(param, params[param]);
    });
    return element;
  }
}
